// One-tap voice → income. The PWA equivalent of "Hey Siri, log farm income".
//
// Flow: page loads → tap mic (or auto-start if granted) → user speaks → server
// parses + auto-saves → confirmation card with the saved row + Undo. Bypasses
// the normal review screen because the whole point is speed.

import { useEffect, useState, useCallback } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import VoiceInput from '../components/VoiceInput.jsx';
import { api, fmtMoney } from '../lib/api.js';

export default function QuickIncome() {
  const { user } = useOutletContext();
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);            // { id, summary, parsed, transcript }
  const [err, setErr] = useState('');

  const onTranscript = useCallback(async (transcript) => {
    setErr(''); setBusy(true);
    try {
      const r = await api.post('/api/voice/quick', { transcript, intent: 'income' });
      setLast(r);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }, []);

  async function undo(id) {
    if (!confirm('Delete this income row?')) return;
    try { await api.delete(`/api/income/${id}`); setLast(null); }
    catch (e) { setErr(e.message); }
  }

  return (
    <div className="space-y-5 max-w-xl mx-auto">
      <header className="text-center pt-6">
        <h1 className="text-3xl font-serif text-olive-900">Quick Voice Income</h1>
        <p className="text-sm text-ink-700/70 mt-1">Hi {user?.name?.split(' ')[0] || ''}. Speak it; we save it.</p>
      </header>

      <div className="card flex flex-col items-center gap-3 py-10">
        <VoiceInput onTranscript={onTranscript} busy={busy} prompt="🎤 Tap to speak" />
        {busy && <p className="text-sm text-ink-700/60 italic">Saving…</p>}
        <p className="text-xs text-ink-700/60 text-center max-w-sm">
          Examples:<br/>
          <em>"Sold three dozen eggs to Amy for twenty bucks cash."</em><br/>
          <em>"Got four hundred fifty dollar USDA payment by check."</em><br/>
          <em>"Sammy bought a beef box for two forty nine, paid Venmo."</em>
        </p>
      </div>

      {err && <div className="card bg-rust-500/10 text-rust-700 text-sm">{err}</div>}

      {last && (
        <div className="card space-y-3 ring-2 ring-olive-500/40">
          <div className="text-olive-900 font-medium">✓ {last.summary}</div>
          <div className="text-sm text-ink-700/80">
            <div><strong>Heard:</strong> "{last.transcript}"</div>
            <ul className="mt-1 text-xs grid grid-cols-2 gap-x-4 gap-y-0.5">
              <li>Customer: {last.parsed.customer_name || '—'}</li>
              <li>Amount: {fmtMoney(last.parsed.total_cents)}</li>
              <li>Segment: {last.parsed.segment_id}</li>
              <li>Sched F: {last.parsed.schedule_f_code}</li>
              <li>Method: {last.parsed.payment_method}</li>
              <li>Confidence: {((last.parsed._confidence ?? 0) * 100).toFixed(0)}%</li>
            </ul>
          </div>
          <div className="flex gap-2">
            <button className="btn-ghost" onClick={() => undo(last.id)}>Undo</button>
            <Link to={`/income`} className="btn-ghost">View register</Link>
            <button className="btn-primary ml-auto" onClick={() => setLast(null)}>Log another</button>
          </div>
        </div>
      )}
    </div>
  );
}
